/**
 * @file messages.ts
 * @description Русские сообщения проверки секции словами владельца
 * (phase19.16 Р2; code-style — интерфейс на русском, 168-ФЗ для покупателя
 * не касается, но кабинет — тоже на русском).
 *
 * Своё сообщение у поля схемы («В колонке не больше 10 элементов») важнее
 * этой карты (zod 4: сообщение схемы приоритетнее карты разбора). Карта
 * переводит то, что zod пишет сам: «нет поля», «не из списка», «слишком
 * длинно», «лишнее поле», «неизвестный вид элемента».
 */

import type { z } from "zod";

/** Пояснения к названиям полей (последний ключ пути) — для «Заполните …». */
const FIELD_NAMES: Readonly<Record<string, string>> = {
  title: "заголовок",
  text: "текст",
  label: "текст кнопки",
  mediaId: "файл из медиатеки",
  url: "ссылку",
  target: "куда ведёт ссылка",
  kind: "вид",
};

/** Карта ошибок разбора для `safeParse(input, { error: sectionErrorMap })`. */
export const sectionErrorMap: z.core.$ZodErrorMap = (issue) => {
  const last = issue.path?.[issue.path.length - 1];
  const field = typeof last === "string" ? FIELD_NAMES[last] : undefined;
  switch (issue.code) {
    case "invalid_type":
      if (issue.input === undefined) return field ? `Заполните ${field}` : "Поле не заполнено";
      return "Неверный тип значения";
    case "invalid_value":
      return "Значение не из списка допустимых";
    case "invalid_union":
      if (issue.path?.length === 0) return "Неизвестный вид элемента";
      // Различитель kind есть и у оформления (высота, фон, заливка) — «вид элемента» только в элементах.
      if (last === "kind") return issue.path?.includes("elements") ? "Неизвестный вид элемента" : "Неизвестный вариант значения";
      if (last === "type") return "Неизвестный вид ссылки";
      return "Значение не подходит ни под один вариант";
    case "too_big":
      if (issue.origin === "string") return `Не длиннее ${String(issue.maximum)} знаков`;
      if (issue.origin === "array") return `Не больше ${String(issue.maximum)}`;
      return `Не больше ${String(issue.maximum)}`;
    case "too_small":
      if (issue.origin === "string") return field ? `Заполните ${field}` : "Поле не заполнено";
      if (issue.origin === "array") return `Нужно хотя бы ${String(issue.minimum)}`;
      return `Не меньше ${String(issue.minimum)}`;
    case "unrecognized_keys":
      return `Лишнее поле: ${issue.keys.join(", ")}`;
    case "invalid_format":
      return "Неверный формат";
    case "not_multiple_of":
      return "Недопустимое значение";
    default:
      return "Значение не прошло проверку";
  }
};
